'use client'
import LoginModal from '@/components/Login/LoginModal'
import { dispatch, useSelector } from '@/lib/store'
import { Fragment, useRef, useState } from 'react'
import CartDrawer from '../CartDrawer'
import {
  askUserToRemoveItems,
  updateCartData,
} from '@/lib/reducers/cartReducer'
import SelectMemberModal from '@/components/modals/SelectMemberModal'
import {
  askUserToAddPatient,
  askUserToSelectPatient,
} from '@/lib/reducers/patientReducer'
import AddNewMemberModal from '@/components/modals/AddNewMemberModal'
import DuplicateTestFoundModal from '@/components/modals/DuplicateTestFoundModal'
import { updatePackageData } from '@/lib/reducers/packageReducer'
import SearchTestsAndPackagesModal from '../SearchTestsAndPackagesModal'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import RequestCallbackModal from '@/components/LandingPage/RequestCallbackModal'
import { usePathname } from 'next/navigation'
import { CHECKOUT_PAGE, FORM_TYPE_LAB_TEST } from '@/utils/constants'
import FullScreenLoader from '../FullScreenLoader'
import CityChangeConfirmationModal from '@/components/CheckoutPage/CityChangeConfirmationModal'
import ConfirmationModal from '../ConfirmationModal'
import CART_HELPER from '@/helper/cartHelper'
import useCustomToast from '@/hooks/useCustomToast'
import { updateOrderData } from '@/lib/reducers/orderReducer'
import { resetLoginData } from '@/lib/reducers/loginReducer'
import ViewCartToast from '../ViewCartToast'
import FullPageInvisibleLoader from '../FullPageInvisibleLoader'
import CommonCenterModal from '@/components/LandingPage/CommonCenterModal'

const ModalsContainer = () => {
  const pathname = usePathname()
  const showToast = useCustomToast()
  const [loading, setLoading] = useState(false)

  const loginState = useSelector((state) => state.login) || {}
  const cartState: any = useSelector((state) => state.cart) || {}
  const patientState = useSelector((state) => state.patient) || {}
  const packageState = useSelector((state) => state.package) || {}
  const commonState = useSelector((state) => state.common) || {}
  const itemToRemoveFromCart = cartState?.itemToRemoveFromCart || {}
  const orderState = useSelector((state) => state.order)
  const CART_ITEMS = cartState?.cartDtoList || []

  const { handleGetCartItems, handleClearCart } = CART_HELPER()

  const handleClearAllProductFromCart = async (cartUuid: string) => {
    setLoading(true)
    const apiResponse: any = await handleClearCart(cartUuid)
    if (apiResponse?.status === true) {
      showToast('success', 'Cart cleared successfully')
      const res = await handleGetCartItems()
      dispatch(
        updateCartData({ ...cartState, ...res?.data, modalIsOpen: false })
      )
      dispatch(
        updateOrderData({
          ...orderState,
          cartDtoList: [...res?.data?.cartDtoList],
        })
      )
      dispatch(askUserToRemoveItems({}))
      setLoading(false)
    } else {
      console.error('Failed to clear cart', apiResponse)
    }
  }

  return (
    <Fragment>
      {!!loginState?.loginModalIsOpen && (
        <LoginModal
          open={loginState?.loginModalIsOpen || false}
          maxWidth={950}
        />
      )}
      {cartState.modalIsOpen && pathname !== CHECKOUT_PAGE && (
        <CartDrawer
          open={cartState.modalIsOpen}
          onOpenChange={() =>
            dispatch(updateCartData({ ...cartState, modalIsOpen: false }))
          }
        />
      )}
      <ConfirmationModal
        open={Object.keys(itemToRemoveFromCart)?.length > 0}
        onOpenChange={() => dispatch(askUserToRemoveItems({}))}
        heading="Remove from cart"
        description={`Are you sure you want to clear ${cartState?.activePatientDetailDto?.firstName}'s cart?`}
        primaryButtonAction={() =>
          handleClearAllProductFromCart(CART_ITEMS?.[0]?.uuid)
        }
      />
      {patientState?.selectPatientModalIsOpen && (
        <SelectMemberModal
          open={patientState?.selectPatientModalIsOpen}
          onOpenChange={() =>
            dispatch(
              askUserToSelectPatient({
                ...patientState,
                selectPatientModalIsOpen: false,
                loginFlowPrimarySelection: false,
              })
            )
          }
        />
      )}
      {patientState?.addNewPatientModalIsOpen && (
        <AddNewMemberModal
          open={patientState?.addNewPatientModalIsOpen}
          onOpenChange={() => {
            dispatch(
              askUserToAddPatient({
                ...patientState,
                addNewPatientModalIsOpen: false,
              })
            )
          }}
        />
      )}
      {!!packageState.productAddingForPatientFlag && (
        <ViewCartToast
          open={!!packageState.productAddingForPatientFlag}
          close={() =>
            dispatch(
              updatePackageData({
                ...packageState,
                productAddingForPatientFlag: false,
              })
            )
          }
        />
      )}
      {packageState?.duplicateProductModalIsOpen && (
        <DuplicateTestFoundModal
          open={packageState?.duplicateProductModalIsOpen}
          onOpenChange={() =>
            dispatch(
              updatePackageData({
                ...packageState,
                duplicateProductModalIsOpen: false,
              })
            )
          }
        />
      )}
      {!!packageState?.modalIsOpen && (
        <SearchTestsAndPackagesModal
          open={packageState?.modalIsOpen}
          onOpenChange={() =>
            dispatch(updatePackageData({ ...packageState, modalIsOpen: false }))
          }
        />
      )}
      {commonState?.requestCallbackModalIsOpen.modalIsOpen && (
        <RequestCallbackModal
          open={commonState?.requestCallbackModalIsOpen.modalIsOpen}
          onOpenChange={() =>
            dispatch(
              updateCommonReducerData({
                ...commonState,
                requestCallbackModalIsOpen: { modalIsOpen: false },
              })
            )
          }
          radiologyRequired={
            commonState?.requestCallbackModalIsOpen?.type !== FORM_TYPE_LAB_TEST
          }
        />
      )}
      {commonState?.userOnboardingModalIsOpen && (
        <AddNewMemberModal
          open={commonState?.userOnboardingModalIsOpen}
          onOpenChange={() => {
            dispatch(
              updateCommonReducerData({
                ...commonState,
                userOnboardingModalIsOpen: false,
              })
            )
            dispatch(resetLoginData())
          }}
          onBoarding={true}
        />
      )}
      {commonState?.commonCenterModalData?.commonCenterModalIsOpen && (
        <CommonCenterModal
          open={commonState?.commonCenterModalData?.commonCenterModalIsOpen}
          onOpenChange={() =>
            dispatch(
              updateCommonReducerData({
                ...commonState,
                commonCenterModalData: {
                  commonCenterModalIsOpen: false,
                  message: '',
                  description: '',
                  smilingEmoticon: false,
                  noIcon: false,
                  callNumber: '',
                },
              })
            )
          }
          message={commonState?.commonCenterModalData?.message || ''}
          description={commonState?.commonCenterModalData?.description || ''}
          smilingEmoticon={
            commonState?.commonCenterModalData?.smilingEmoticon || false
          }
          error={commonState?.commonCenterModalData?.error || false}
          noIcon={commonState?.commonCenterModalData?.noIcon || false}
          callNumber={commonState?.commonCenterModalData?.callNumber || ''}

          //   message="Request submitted successfully"
          //   description="Stay tuned, our health advisor will call you shortly to create your order"
        />
      )}
      {commonState.globalLoaderIsOpen && (
        <FullScreenLoader open={commonState.globalLoaderIsOpen} />
      )}
      {!!commonState.updatedCityUuid && (
        <CityChangeConfirmationModal
          open={!!commonState.updatedCityUuid}
          onOpenChange={() =>
            dispatch(
              updateCommonReducerData({
                ...commonState,
                updatedCityUuid: '',
              })
            )
          }
        />
      )}
    </Fragment>
  )
}

export default ModalsContainer
